import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  Divider
} from '@mui/material';
import { toast } from 'react-toastify';
import { schemesV2Api, schemesV2Utils } from '../../services/schemesV2Api';
import { generalToastStyle } from '../../utils/toast.styles';
import Navbar from '../../components/navbar/navbar.component';

/**
 * SchemesV2Test Component
 * 
 * This component provides a testing interface for the Razorpay integration.
 * It should only be used in development/testing environments.
 */
const SchemesV2Test = () => {
  const [testResults, setTestResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [schemes, setSchemes] = useState([]);
  const [currentRates, setCurrentRates] = useState(null);

  useEffect(() => {
    loadTestData();
  }, []);

  const loadTestData = async () => {
    try {
      setIsLoading(true);
      const [schemesResponse, ratesResponse] = await Promise.all([
        schemesV2Api.getAllSchemes(),
        schemesV2Api.getCurrentRates()
      ]);
      
      setSchemes(schemesResponse.data.schemes || []);
      setCurrentRates(ratesResponse.data);
    } catch (error) {
      addTestResult('Data Loading', false, `Failed to load test data: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const addTestResult = (testName, success, message, details = null) => {
    const result = {
      id: Date.now(),
      testName,
      success,
      message,
      details,
      timestamp: new Date().toLocaleTimeString()
    };
    setTestResults(prev => [result, ...prev]);
  };

  const testRazorpayScriptLoading = async () => {
    try {
      addTestResult('Razorpay Script Loading', true, 'Testing Razorpay script loading...');
      
      const loaded = await schemesV2Utils.loadRazorpayScript();
      if (loaded) {
        addTestResult('Razorpay Script Loading', true, 'Razorpay script loaded successfully');
      } else {
        addTestResult('Razorpay Script Loading', false, 'Failed to load Razorpay script');
      }
    } catch (error) {
      addTestResult('Razorpay Script Loading', false, `Error loading Razorpay script: ${error.message}`);
    }
  };

  const testRazorpayInitialization = () => {
    try {
      addTestResult('Razorpay Initialization', true, 'Testing Razorpay initialization...');
      
      if (!window.Razorpay) {
        addTestResult('Razorpay Initialization', false, 'Razorpay SDK not available');
        return;
      }

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY,
        amount: 10000, // ₹100
        currency: 'INR',
        name: 'Test Payment',
        description: 'Test payment for Schemes V2',
        handler: function(response) {
          addTestResult('Razorpay Initialization', true, 'Razorpay initialized successfully', response);
        }
      };

      const razorpay = schemesV2Utils.initializeRazorpayPayment(options);
      addTestResult('Razorpay Initialization', true, 'Razorpay instance created successfully');
    } catch (error) {
      addTestResult('Razorpay Initialization', false, `Error initializing Razorpay: ${error.message}`);
    }
  };

  const testOrderCreation = async () => {
    try {
      addTestResult('Order Creation', true, 'Testing Razorpay order creation...');
      
      const orderData = {
        enrollmentId: 999, // Test enrollment ID
        installmentNumber: 1,
        amount: 1000
      };

      const response = await schemesV2Api.createRazorpayOrder(orderData);
      addTestResult('Order Creation', true, 'Order created successfully', response.data);
    } catch (error) {
      addTestResult('Order Creation', false, `Failed to create order: ${error.message}`);
    }
  };

  const testPaymentVerification = async () => {
    try {
      addTestResult('Payment Verification', true, 'Testing payment verification...');
      
      const verificationData = {
        razorpay_order_id: 'order_test123',
        razorpay_payment_id: 'pay_test123',
        razorpay_signature: 'test_signature',
        enrollment_id: 999,
        installment_number: 1
      };

      const response = await schemesV2Api.verifyPayment(verificationData);
      addTestResult('Payment Verification', true, 'Payment verified successfully', response.data);
    } catch (error) {
      addTestResult('Payment Verification', false, `Failed to verify payment: ${error.message}`);
    }
  };

  const testSubscriptionCreation = async () => {
    try {
      addTestResult('Subscription Creation', true, 'Testing subscription creation...');
      
      const subscriptionData = {
        enrollmentId: 999,
        instalmentAmount: 1000,
        schemeType: 'monthly',
        tenureDays: 30,
        customerId: 'cust_test123'
      };

      const response = await schemesV2Api.createSubscription(subscriptionData);
      addTestResult('Subscription Creation', true, 'Subscription created successfully', response.data);
    } catch (error) {
      addTestResult('Subscription Creation', false, `Failed to create subscription: ${error.message}`);
    }
  };

  const testUtilityFunctions = () => {
    try {
      addTestResult('Utility Functions', true, 'Testing utility functions...');
      
      // Test currency formatting
      const formattedCurrency = schemesV2Utils.formatCurrency(1000);
      addTestResult('Currency Formatting', true, `Formatted currency: ${formattedCurrency}`);
      
      // Test date formatting
      const formattedDate = schemesV2Utils.formatDate('2025-01-16');
      addTestResult('Date Formatting', true, `Formatted date: ${formattedDate}`);
      
      // Test progress calculation
      const progress = schemesV2Utils.calculateProgress(5, 10);
      addTestResult('Progress Calculation', true, `Progress: ${progress}%`);
      
      // Test gold grams calculation
      const goldGrams = schemesV2Utils.calculateGoldGrams(1000, 6250);
      addTestResult('Gold Grams Calculation', true, `Gold grams: ${goldGrams.toFixed(4)}`);
      
      // Test receipt ID generation
      const receiptId = schemesV2Utils.generateReceiptId(123, 1);
      addTestResult('Receipt ID Generation', true, `Generated receipt ID: ${receiptId}`);
      
      // Test Razorpay response validation
      const validResponse = {
        razorpay_payment_id: 'pay_123',
        razorpay_order_id: 'order_123',
        razorpay_signature: 'sig_123'
      };
      schemesV2Utils.validateRazorpayResponse(validResponse);
      addTestResult('Razorpay Response Validation', true, 'Valid response validated successfully');
      
    } catch (error) {
      addTestResult('Utility Functions', false, `Error testing utility functions: ${error.message}`);
    }
  };

  const testWebhookSimulation = () => {
    try {
      addTestResult('Webhook Simulation', true, 'Testing webhook event simulation...');
      
      // Simulate payment captured webhook
      const paymentWebhook = {
        event: 'payment.captured',
        payload: {
          payment: {
            id: 'pay_test123',
            amount: 100000,
            currency: 'INR',
            status: 'captured'
          },
          order: {
            id: 'order_test123',
            notes: {
              enrollment_id: '999',
              installment_number: '1'
            }
          }
        }
      };
      
      const event = new CustomEvent('razorpay-webhook', {
        detail: paymentWebhook
      });
      window.dispatchEvent(event);
      
      addTestResult('Webhook Simulation', true, 'Payment captured webhook simulated');
      
      // Simulate subscription activated webhook
      const subscriptionWebhook = {
        event: 'subscription.activated',
        payload: {
          subscription: {
            id: 'sub_test123',
            status: 'active'
          }
        }
      };
      
      const subEvent = new CustomEvent('razorpay-webhook', {
        detail: subscriptionWebhook
      });
      window.dispatchEvent(subEvent);
      
      addTestResult('Webhook Simulation', true, 'Subscription activated webhook simulated');
      
    } catch (error) {
      addTestResult('Webhook Simulation', false, `Error simulating webhooks: ${error.message}`);
    }
  };

  const runAllTests = async () => {
    setTestResults([]);
    setIsLoading(true);
    
    try {
      await testRazorpayScriptLoading();
      await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
      
      testRazorpayInitialization();
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      await testOrderCreation();
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      await testPaymentVerification();
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      await testSubscriptionCreation();
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      testUtilityFunctions();
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      testWebhookSimulation();
      
      addTestResult('All Tests', true, 'All tests completed successfully!');
    } catch (error) {
      addTestResult('All Tests', false, `Error running tests: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const clearResults = () => {
    setTestResults([]);
  };

  const getStatusColor = (success) => {
    return success ? '#4caf50' : '#f44336';
  };

  const getStatusChip = (success) => {
    return (
      <Chip
        label={success ? 'PASS' : 'FAIL'}
        size="small"
        sx={{
          backgroundColor: success ? '#e8f5e8' : '#ffebee',
          color: success ? '#2e7d32' : '#c62828',
          fontWeight: 600
        }}
      />
    );
  };

  return (
    <Container maxWidth="xl" disableGutters>
      <Navbar />
      
      <Box
        sx={{
          background: "linear-gradient(to bottom, rgb(249, 236, 220), rgb(231, 192, 125))",
          minHeight: "100vh",
          pt: { xs: 15, md: 12 },
          pb: 4
        }}
      >
        <Container maxWidth="lg">
          {/* Header */}
          <Box sx={{ mb: 4, textAlign: "center" }}>
            <Typography
              variant="h4"
              sx={{
                color: "#A36E29",
                fontWeight: 700,
                mb: 1,
                fontFamily: '"Roboto", sans-serif'
              }}
            >
              Schemes V2 Razorpay Integration Test
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "rgba(163, 110, 41, 0.8)",
                fontFamily: '"Roboto", sans-serif'
              }}
            >
              Test the complete Razorpay integration for Schemes V2
            </Typography>
          </Box>

          {/* Test Controls */}
          <Card sx={{ mb: 3, borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Test Controls
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={runAllTests}
                    disabled={isLoading}
                    sx={{
                      background: "linear-gradient(to right, #d4a76a, #A36E29)",
                      mb: 2
                    }}
                  >
                    {isLoading ? (
                      <>
                        <CircularProgress size={20} sx={{ color: "white", mr: 1 }} />
                        Running Tests...
                      </>
                    ) : (
                      'Run All Tests'
                    )}
                  </Button>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={clearResults}
                    sx={{ borderColor: "#A36E29", color: "#A36E29" }}
                  >
                    Clear Results
                  </Button>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Button
                    variant="outlined"
                    onClick={testRazorpayScriptLoading}
                    sx={{ borderColor: "#A36E29", color: "#A36E29", mb: 1 }}
                    fullWidth
                  >
                    Test Script Loading
                  </Button>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Button
                    variant="outlined"
                    onClick={testRazorpayInitialization}
                    sx={{ borderColor: "#A36E29", color: "#A36E29", mb: 1 }}
                    fullWidth
                  >
                    Test Initialization
                  </Button>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Button
                    variant="outlined"
                    onClick={testOrderCreation}
                    sx={{ borderColor: "#A36E29", color: "#A36E29", mb: 1 }}
                    fullWidth
                  >
                    Test Order Creation
                  </Button>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Button
                    variant="outlined"
                    onClick={testPaymentVerification}
                    sx={{ borderColor: "#A36E29", color: "#A36E29", mb: 1 }}
                    fullWidth
                  >
                    Test Payment Verification
                  </Button>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Button
                    variant="outlined"
                    onClick={testSubscriptionCreation}
                    sx={{ borderColor: "#A36E29", color: "#A36E29", mb: 1 }}
                    fullWidth
                  >
                    Test Subscription Creation
                  </Button>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Button
                    variant="outlined"
                    onClick={testUtilityFunctions}
                    sx={{ borderColor: "#A36E29", color: "#A36E29", mb: 1 }}
                    fullWidth
                  >
                    Test Utility Functions
                  </Button>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Button
                    variant="outlined"
                    onClick={testWebhookSimulation}
                    sx={{ borderColor: "#A36E29", color: "#A36E29", mb: 1 }}
                    fullWidth
                  >
                    Test Webhook Simulation
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Test Results */}
          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Test Results
              </Typography>
              
              {testResults.length === 0 ? (
                <Alert severity="info">
                  No test results yet. Click "Run All Tests" to start testing.
                </Alert>
              ) : (
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {testResults.map((result) => (
                    <Paper
                      key={result.id}
                      sx={{
                        p: 2,
                        mb: 2,
                        borderLeft: `4px solid ${getStatusColor(result.success)}`,
                        backgroundColor: result.success ? '#f8f9fa' : '#fff5f5'
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {result.testName}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getStatusChip(result.success)}
                          <Typography variant="caption" color="text.secondary">
                            {result.timestamp}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {result.message}
                      </Typography>
                      
                      {result.details && (
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                          {JSON.stringify(result.details, null, 2)}
                        </Typography>
                      )}
                    </Paper>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Environment Info */}
          <Card sx={{ mt: 3, borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Environment Information
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Razorpay Key ID:
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                    {process.env.REACT_APP_RAZORPAY_KEY || 'Not configured'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    API Base URL:
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                    {process.env.REACT_APP_API_URL}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Available Schemes:
                  </Typography>
                  <Typography variant="body1">
                    {schemes.length} schemes loaded
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Current Gold Rate:
                  </Typography>
                  <Typography variant="body1">
                    {currentRates?.rates?.gold22k ? `₹${currentRates.rates.gold22k}` : 'Not available'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Container>
      </Box>
    </Container>
  );
};

export default SchemesV2Test;
